import React, { useState } from 'react'

import { formatPrice } from '../../../../common/utils/utils.js'
import { Accordion, AccordionItem } from '../../../components/accordion/accordion.js'
import { Button } from '../../../components/button/button.js'
import { GridColumn } from '../../../components/grid/grid-column.js'
import { Grid } from '../../../components/grid/grid.js'
import { useCurrentBreakPoint } from '../../../components/helper/hooks/use-current-breakpoint.js'
import { InfoIcon } from '../../../components/icon/icon.js'
import { Separator } from '../../../components/separator/separator.js'
import { Spinner } from '../../../components/spinner/spinner.js'
import { Tooltip } from '../../../components/tooltip/tooltip.js'
import { Invoice, SimpleInvoiceStatus } from '../../../models/invoice.js'
import { ViewBase, ViewBaseProps } from '../../base/base.js'

export interface AccordionItemProps {
    title: string
    text: string
}

export interface InvoicesProps {
    title: string
    invoices: Invoice[]
    tableHeader: string[]
    viewInvoiceText: string
    accordion: AccordionItemProps
    isLoading: boolean
    noInvoices: string
    loadMore: string
}

export interface ViewInvoicesProps extends ViewBaseProps, InvoicesProps {}

const InvoiceDescriptionCell = (props: { invoice: Invoice; isMobile: boolean }): JSX.Element => {
    const { invoice, isMobile } = props
    const { deviceNameTruncated, deviceLines } = invoice

    return (
        <div className="invoices__device-wrapper" style={isMobile ? { justifyContent: 'flex-end' } : {}}>
            {deviceNameTruncated}
            {deviceLines !== 'No items' && (
                <span className="invoices__device-icon">
                    <Tooltip triggerEvent="hover" element="span" trigger={<InfoIcon />}>
                        <div className="invoices__tooltip-content">
                            {invoice.data.lines?.map((line, idx) => (
                                <div key={line.id}>
                                    <div className="invoices__tooltip-item">
                                        <div className="invoices__tooltip-description">{line.description}</div>
                                        <div className="invoices__tooltip-amount">
                                            {formatPrice(line.totalWithVat, invoice.currency)}
                                        </div>
                                    </div>
                                    {idx < (invoice.data.lines?.length || 0) - 1 && (
                                        <div className="invoices__tooltip-separator" />
                                    )}
                                </div>
                            ))}
                            <div className="invoices__tooltip-total">
                                <div className="invoices__tooltip-total-row">
                                    <span>Total:</span>
                                    <span>{invoice.formattedAmount}</span>
                                </div>
                            </div>
                        </div>
                    </Tooltip>
                </span>
            )}
        </div>
    )
}

export const Invoices = (props: InvoicesProps): JSX.Element => {
    const [visibleItems, setVisibleItems] = useState(10)

    const itemsPerPage = 10
    const shouldRenderLoadMore = props.invoices.length > visibleItems
    const isEmpty = props.invoices.length === 0

    const bp = useCurrentBreakPoint()
    const isMobile = bp ? ['xs', 'sm'].includes(bp) : true

    const handleLoadMore = () => {
        setVisibleItems((prevVisibleItems) => prevVisibleItems + itemsPerPage)
    }

    const renderSpinnerOrEmptyState = (isLoading: boolean): JSX.Element | null => {
        if (isLoading) {
            return (
                <div className="loading">
                    <Spinner variant="secondary" />
                </div>
            )
        }

        if (isEmpty) {
            return <p className="invoices__no-items-message">{props.noInvoices}</p>
        }

        return null
    }

    const renderBadge = (simpleStatus: SimpleInvoiceStatus): JSX.Element => (
        <div className={`status status--color-${simpleStatus.color}`}>{simpleStatus.text}</div>
    )

    const renderTable = (tableHeader: string[], invoices: Invoice[]): JSX.Element => (
        <table className="invoices__table">
            <thead>
                <tr>
                    {tableHeader.map((item, index: number) => (
                        <th key={index} align={`${tableHeader.length / 2 < index ? 'right' : 'left'}`}>
                            {item}
                        </th>
                    ))}
                </tr>
                <tr>
                    <td colSpan={tableHeader.length}>
                        <Separator invisible variant="slim" />
                    </td>
                </tr>
            </thead>
            <tbody>
                {invoices.slice(0, visibleItems).map((invoice) => {
                    const { ID, formattedDueDate, formattedAmount, pdfUrl, period, simplifiedStatus } = invoice

                    return (
                        <React.Fragment key={ID}>
                            <tr className={`${simplifiedStatus.color === 'orange' ? 'text--weight-bold' : ''}`}>
                                <td>{ID}</td>
                                <td>
                                    <InvoiceDescriptionCell invoice={invoice} isMobile={false} />
                                </td>
                                <td>{period}</td>
                                <td>{formattedDueDate}</td>
                                <td className="text--weight-bold" align="right">
                                    {formattedAmount}
                                </td>
                                <td align="right">{renderBadge(simplifiedStatus)}</td>
                                <td>
                                    <div className="button--wrapper">
                                        <Button
                                            text={props.viewInvoiceText}
                                            url={pdfUrl}
                                            variant="secondary"
                                            isBlock={!!isMobile}
                                            isDisabled={!pdfUrl}
                                        />
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td colSpan={tableHeader.length + 1}>
                                    <Separator variant="slim" />
                                </td>
                            </tr>
                        </React.Fragment>
                    )
                })}
            </tbody>
        </table>
    )

    const renderMobileTable = (tableHeader: string[], invoices: Invoice[]): JSX.Element => (
        <table className="invoices__table">
            <tbody>
                {invoices.slice(0, visibleItems).map((invoice, index) => {
                    const { ID, formattedAmount, period, formattedDueDate, pdfUrl, simplifiedStatus } = invoice

                    return (
                        <React.Fragment key={index}>
                            <tr>
                                <th align="left">{tableHeader[0]}</th>
                                <td align="right">
                                    <div className="items--flex-end items--align-center">
                                        {renderBadge(simplifiedStatus)}
                                        {ID}
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <th align="left">{tableHeader[1]}</th>
                                <td align="right">
                                    <InvoiceDescriptionCell invoice={invoice} isMobile={true} />
                                </td>
                            </tr>
                            <tr>
                                <th align="left">{tableHeader[2]}</th>
                                <td align="right">{period}</td>
                            </tr>
                            <tr>
                                <th align="left">{tableHeader[3]}</th>
                                <td align="right">{formattedDueDate}</td>
                            </tr>
                            <tr>
                                <th align="left">{tableHeader[4]}</th>
                                <td align="right" className="text--weight-bold">
                                    {formattedAmount}
                                </td>
                            </tr>
                            <tr>
                                <td colSpan={2}>
                                    <Button text={props.viewInvoiceText} url={pdfUrl} variant="secondary" isBlock />
                                </td>
                            </tr>
                            <tr>
                                <td colSpan={2}>
                                    <Separator variant="large" />
                                </td>
                            </tr>
                        </React.Fragment>
                    )
                })}
            </tbody>
        </table>
    )

    const renderAccordion = (accordion: AccordionItemProps) => (
        <Accordion>
            <AccordionItem id="accordion-item-1" label={accordion.title} className="text--weight-bold">
                {accordion.text}
            </AccordionItem>
        </Accordion>
    )

    return (
        <div className="invoices text">
            <Grid align={['middle-xs']} noWrap={true}>
                <GridColumn width={['lg-12']}>
                    <h2>{props.title}</h2>
                </GridColumn>
            </Grid>
            <Grid className="text__margin-double">
                <GridColumn width={['lg-12']}>
                    <div className="text">
                        {isMobile
                            ? renderMobileTable(props.tableHeader, props.invoices)
                            : renderTable(props.tableHeader, props.invoices)}
                        {(isEmpty || props.isLoading) && renderSpinnerOrEmptyState(props.isLoading)}
                        {shouldRenderLoadMore && (
                            <div className="invoices__load-more text-center">
                                <Button
                                    text={props.loadMore}
                                    onClick={handleLoadMore}
                                    variant="secondary"
                                    isBlock={isMobile}
                                />
                            </div>
                        )}
                    </div>
                </GridColumn>
            </Grid>
            {props.accordion.title && props.accordion.text && (
                <Grid align={['middle-xs']} noWrap={true}>
                    <GridColumn width={['lg-12']}>{renderAccordion(props.accordion)}</GridColumn>
                </Grid>
            )}
        </div>
    )
}

export const ViewInvoices = (props: ViewInvoicesProps): JSX.Element => {
    return (
        <ViewBase {...props}>
            <Invoices {...props} />
        </ViewBase>
    )
}
